import { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Paper,
  Typography,
  Box,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import axiosClient from "../../api/axiosClient";
import { useLocation, useNavigate } from "react-router-dom";
import { isLoggedIn } from "../../utils/auth";
import LoginResponse from "./LoginResponse";
import LoginRequest from "./LoginRequest";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (isLoggedIn()) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  // Show error if redirected from PrivateRoute
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axiosClient.post(
        "/auth/login",
        new LoginRequest(username, password),
      );

      //here i use a ts model to wrap the payload
      const data = new LoginResponse(response.data);

      if (data.operationResult) {
        // Store token
        localStorage.setItem("token", `${data.type} ${data.token}`);
        console.log(
          "Login successful, token saved:",
          `${data.type} ${data.token}`,
        );

        navigate("/dashboard");
      } else {
        console.error("Login failed:", data.errorCode);
        setError(data.errorMsg || "Login failed. Please try again.");
      }
    } catch (err) {
      console.error("Login failed:", err);
      setError("Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" height="100vh" bgcolor="#e9f0f7">
      <Header />

      <Box
        flexGrow={1}
        display="flex"
        justifyContent="center"
        alignItems="center"
        px={2}
      >
        <Paper
          elevation={6}
          sx={{
            p: { xs: 2.5, sm: 4 },
            width: "100%",
            maxWidth: 380,
            borderRadius: 2,
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Avatar sx={{ bgcolor: "#1976d2", mb: 1 }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography variant="h6" gutterBottom>
              Login
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              label="Username"
              variant="outlined"
              fullWidth
              margin="normal"
              value={username}
              disabled={loading}
              onChange={(e) => setUsername(e.target.value)}
            />
            <TextField
              label="Password"
              type="password"
              variant="outlined"
              fullWidth
              margin="normal"
              value={password}
              disabled={loading}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                mt: 2,
                py: 1,
                fontWeight: "bold",
                textTransform: "none",
                bgcolor: "#1976d2",
                "&:hover": { bgcolor: "#125ea5" },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Login"
              )}
            </Button>
          </form>
        </Paper>
      </Box>

      <Footer />
    </Box>
  );
}
